import { doc, getDoc, writeBatch, onSnapshot, serverTimestamp, type Timestamp } from 'firebase/firestore';
import { db, auth } from './config';
import { PUBLIC_API_BASE_URL } from '$env/static/public';
import type { UserProfileData } from '$lib/stores/auth.svelte';

export interface UserDocument extends UserProfileData {
	uid: string;
	createdAt: Timestamp;
	updatedAt: Timestamp;
}

export async function getUserProfile(userId: string): Promise<UserDocument | null> {
	const ref = doc(db(), 'users', userId);
	const snap = await getDoc(ref);
	if (!snap.exists()) return null;
	return { uid: userId, ...snap.data() } as UserDocument;
}

export async function updateUserProfile(
	userId: string,
	data: Partial<UserProfileData>
): Promise<void> {
	const ref = doc(db(), 'users', userId);
	const privateRef = doc(db(), 'users', userId, 'private', 'data');

	const clean = { ...data };
	delete clean.handle;

	const publicFields: Record<string, unknown> = {};
	if (clean.displayName !== undefined) publicFields.displayName = clean.displayName;
	if (clean.photoURL !== undefined) publicFields.photoURL = clean.photoURL;
	if (clean.paymentLinks !== undefined) publicFields.paymentLinks = clean.paymentLinks;

	const privateFields: Record<string, unknown> = {};
	if (clean.email !== undefined) privateFields.email = clean.email;
	if (clean.defaultCurrency !== undefined) privateFields.defaultCurrency = clean.defaultCurrency;

	const hasPublicUpdates = Object.keys(publicFields).length > 0;
	const hasPrivateUpdates = Object.keys(privateFields).length > 0;

	if (!hasPublicUpdates && !hasPrivateUpdates) return;

	const batch = writeBatch(db());
	if (hasPublicUpdates) {
		batch.update(ref, { ...publicFields, updatedAt: serverTimestamp() });
	}
	if (hasPrivateUpdates) {
		batch.set(privateRef, { ...privateFields, updatedAt: serverTimestamp() }, { merge: true });
	}
	await batch.commit();
}

export function subscribeToUserProfile(
	userId: string,
	onUpdate: (profile: UserDocument | null) => void
): () => void {
	const ref = doc(db(), 'users', userId);
	return onSnapshot(
		ref,
		(snap) => {
			if (snap.exists()) {
				onUpdate({ uid: userId, ...snap.data() } as UserDocument);
			} else {
				onUpdate(null);
			}
		},
		(error) => {
			console.error('Error listening to user profile:', error);
			onUpdate(null);
		}
	);
}

export async function lookupHandle(handle: string): Promise<{ uid: string; profile: UserDocument } | null> {
	const handleRef = doc(db(), 'handles', handle.toLowerCase());
	const handleSnap = await getDoc(handleRef);
	if (!handleSnap.exists()) return null;
	const uid = handleSnap.data().uid as string;
	const profile = await getUserProfile(uid);
	if (!profile) return null;
	if (profile.status === 'anonymized') return null;
	return { uid, profile };
}

export async function createAccountViaApi(
	email: string,
	password: string,
	displayName: string,
	handle: string
): Promise<void> {
	const res = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/users/create-account`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ email, password, displayName, handle })
	});
	const data = await res.json();
	if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to create account');
}

export async function setHandleViaApi(
	handle: string,
	displayName?: string,
	email?: string
): Promise<string> {
	const user = auth().currentUser;
	if (!user) throw new Error('Not authenticated');
	const token = await user.getIdToken();
	const res = await fetch(`${PUBLIC_API_BASE_URL}/api/v1/users/set-handle`, {
		method: 'POST',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify({ handle, displayName, email })
	});
	const data = await res.json();
	if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to set handle');
	return data.data.handle as string;
}
